import { useEffect, useCallback, useMemo, useRef, useState, lazy, Suspense } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import { useI18n } from "@/i18n/I18nContext";
import { LogOut, Users, Receipt, Activity, Loader2, Settings as SettingsIcon, LayoutDashboard, Package, Wrench, Upload, Image as ImageIcon, BookOpen, MessageSquareHeart, AlertTriangle, Wallet, Shield, PiggyBank, Building2, Crown, TrendingUp, Megaphone, ChevronDown, ShieldCheck, ServerCrash, Database, ShoppingCart, LifeBuoy, Stethoscope, Inbox, Link2 } from "lucide-react";
import { toast } from "sonner";
// Dashboard is the default landing tab and Settings is small + visited often →
// keep them eager so the most common navigations don't show a Suspense flash.
import Dashboard from "@/pages/admin/Dashboard";
import Settings from "@/pages/admin/Settings";
// Everything else is lazy. Each tab becomes its own webpack chunk; the user
// only downloads the JS for tabs they actually open. React.lazy requires a
// default export — for the one named export we use (`Payroll` from Finance),
// re-shape the module to expose it as `default`.
const Cases          = lazy(() => import("@/pages/admin/Cases"));
const Clients        = lazy(() => import("@/pages/admin/Clients"));
const Invoices       = lazy(() => import("@/pages/admin/Invoices"));
const Products       = lazy(() => import("@/pages/admin/Products"));
const Technicians    = lazy(() => import("@/pages/admin/Technicians"));
const Branding       = lazy(() => import("@/pages/admin/Branding"));
const Feedback       = lazy(() => import("@/pages/admin/Feedback"));
const Errors         = lazy(() => import("@/pages/admin/Errors"));
const AdminCredits   = lazy(() => import("@/pages/admin/Credits"));
const AdminAuditLog  = lazy(() => import("@/pages/admin/AuditLog"));
const Finance        = lazy(() => import("@/pages/admin/Finance"));
const Payroll        = lazy(() => import("@/pages/admin/Payroll"));
const Team           = lazy(() => import("@/pages/admin/Team"));
const TeamMembers    = lazy(() => import("@/pages/admin/TeamMembers"));
const Manual         = lazy(() => import("@/pages/admin/Manual"));
const MediaLibrary   = lazy(() => import("@/pages/admin/MediaLibrary"));
const Sales          = lazy(() => import("@/pages/admin/Sales"));
const Doctors        = lazy(() => import("@/pages/admin/Doctors"));
const DataPrivacy    = lazy(() => import("@/pages/admin/DataPrivacy"));
const DisasterRecovery = lazy(() => import("@/pages/admin/DisasterRecovery"));
const Database_      = lazy(() => import("@/pages/admin/Database"));
const Marketing      = lazy(() => import("@/pages/admin/Marketing"));
const Procurement    = lazy(() => import("@/pages/admin/Procurement"));
const SupportInbox   = lazy(() => import("@/pages/admin/SupportInbox"));
const Tenants        = lazy(() => import("@/pages/admin/Tenants"));
const ScanInbox      = lazy(() => import("@/pages/admin/ScanInbox"));
const Connections    = lazy(() => import("@/pages/admin/Connections"));
const WarrantyClaims = lazy(() => import("@/pages/admin/WarrantyClaims"));
const Subscription   = lazy(() => import("@/pages/admin/Subscription"));
const PlatformMetrics = lazy(() => import("@/pages/admin/PlatformMetrics"));
const AdminTrust     = lazy(() => import("@/pages/admin/AdminTrust"));
const FinanceModel   = lazy(() => import("@/pages/admin/FinanceModel"));
import DrillDrawer from "@/components/DrillDrawer";
import OpsCriticalBadge from "@/components/OpsCriticalBadge";
import PreviewBanner from "@/components/PreviewBanner";
import CommandPalette from "@/components/CommandPalette";
import ViewAsSwitcher from "@/components/ViewAsSwitcher";
import ManualsMenu from "@/components/ManualsMenu";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Accent palette for the categorized admin nav groups. Full Tailwind class
// strings (not interpolated) so the JIT keeps them in the build.
const NAV_ACCENTS = {
    sky:     { icon: "text-sky-500",     iconHover: "group-hover:text-sky-600",     header: "text-sky-600",     leftBar: "bg-sky-500",     activeBg: "bg-sky-50",     activeFocusBg: "focus:bg-sky-50",     activeIcon: "text-sky-600",     activeDot: "bg-sky-500" },
    indigo:  { icon: "text-indigo-500",  iconHover: "group-hover:text-indigo-600",  header: "text-indigo-600",  leftBar: "bg-indigo-500",  activeBg: "bg-indigo-50",  activeFocusBg: "focus:bg-indigo-50",  activeIcon: "text-indigo-600",  activeDot: "bg-indigo-500" },
    emerald: { icon: "text-emerald-500", iconHover: "group-hover:text-emerald-600", header: "text-emerald-600", leftBar: "bg-emerald-500", activeBg: "bg-emerald-50", activeFocusBg: "focus:bg-emerald-50", activeIcon: "text-emerald-600", activeDot: "bg-emerald-500" },
    violet:  { icon: "text-violet-500",  iconHover: "group-hover:text-violet-600",  header: "text-violet-600",  leftBar: "bg-violet-500",  activeBg: "bg-violet-50",  activeFocusBg: "focus:bg-violet-50",  activeIcon: "text-violet-600",  activeDot: "bg-violet-500" },
    amber:   { icon: "text-amber-500",   iconHover: "group-hover:text-amber-600",   header: "text-amber-600",   leftBar: "bg-amber-500",   activeBg: "bg-amber-50",   activeFocusBg: "focus:bg-amber-50",   activeIcon: "text-amber-600",   activeDot: "bg-amber-500" },
    rose:    { icon: "text-rose-500",    iconHover: "group-hover:text-rose-600",    header: "text-rose-600",    leftBar: "bg-rose-500",    activeBg: "bg-rose-50",    activeFocusBg: "focus:bg-rose-50",    activeIcon: "text-rose-600",    activeDot: "bg-rose-500" },
};

export default function Admin() {
    const { user, logout, loading: authLoading, hasPerm, isSuperadmin, isPlatformAdmin } = useAuth();
    const { t } = useI18n();
    const nav = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const [tab, setTab] = useState(() => searchParams.get("tab") || "dashboard");

    // Hover-to-open nav dropdowns. `openGroup` controls which group is open;
    // a short close-delay timer lets the cursor travel from trigger to menu
    // without the dropdown snapping shut. Clicks still work for keyboard /
    // touch users — Radix's `onOpenChange` is wired up alongside the hover.
    const [openGroup, setOpenGroup] = useState(null);
    const closeTimerRef = useRef(null);
    const cancelCloseTimer = useCallback(() => {
        if (closeTimerRef.current) {
            clearTimeout(closeTimerRef.current);
            closeTimerRef.current = null;
        }
    }, []);
    const openNavGroup = useCallback((id) => {
        cancelCloseTimer();
        setOpenGroup(id);
    }, [cancelCloseTimer]);
    const closeNavNow = useCallback(() => {
        cancelCloseTimer();
        setOpenGroup(null);
    }, [cancelCloseTimer]);
    // 80ms is just enough for the cursor to bridge the 6px gap between
    // trigger and menu content without snapping shut — but small enough that
    // a fast click on the page below isn't intercepted by a lingering portal.
    const scheduleCloseNav = useCallback(() => {
        cancelCloseTimer();
        closeTimerRef.current = setTimeout(() => setOpenGroup(null), 80);
    }, [cancelCloseTimer]);
    useEffect(() => () => { if (closeTimerRef.current) clearTimeout(closeTimerRef.current); }, []);

    // Robust "mouse moved away" detector — fixes the floating menu staying
    // stuck open when the cursor leaves the menu through certain paths that
    // Radix's Portal doesn't fire `onMouseLeave` for (e.g., transitioning
    // diagonally to a far area, or moving fast across the page). Active only
    // while a group is open. We compare cursor coords to the VISIBLE bounding
    // rect of the menu rather than using elementFromPoint, because Radix's
    // popper-content-wrapper has invisible spillover that extends well past
    // the menu's visible right edge (and would falsely keep the menu open).
    useEffect(() => {
        if (!openGroup) return undefined;
        const triggerSel = `[data-testid="admin-tab-group-${openGroup}"]`;
        const menuSel = `[data-testid="admin-tab-group-${openGroup}-menu"]`;
        const PAD = 6; // px of forgiveness — covers the 6px sideOffset bridge.
        const inRect = (r, x, y) =>
            r && x >= r.left - PAD && x <= r.right + PAD && y >= r.top - PAD && y <= r.bottom + PAD;
        const handler = (e) => {
            const trigger = document.querySelector(triggerSel);
            const menu = document.querySelector(menuSel);
            const tRect = trigger ? trigger.getBoundingClientRect() : null;
            const mRect = menu ? menu.getBoundingClientRect() : null;
            const onTrigger = inRect(tRect, e.clientX, e.clientY);
            const onMenu = inRect(mRect, e.clientX, e.clientY);
            if (onTrigger || onMenu) {
                cancelCloseTimer();
            } else {
                scheduleCloseNav();
            }
        };
        // Window blur catches alt-tab / browser-switch — close so it isn't
        // sitting there when the user comes back to a different page.
        const onBlur = () => closeNavNow();
        document.addEventListener("pointermove", handler, true);
        window.addEventListener("blur", onBlur);
        return () => {
            document.removeEventListener("pointermove", handler, true);
            window.removeEventListener("blur", onBlur);
        };
    }, [openGroup, cancelCloseTimer, scheduleCloseNav, closeNavNow]);

    // Cross-component tab switching: the Ops Health widget on the dashboard
    // emits `admin-switch-tab` when a user clicks a hash-link (e.g. /admin#sales).
    // We listen here and switch the active tab. Keeps the dashboard widget free
    // of any Admin-internal state.
    useEffect(() => {
        const handler = (e) => {
            const t = e?.detail?.tabId;
            if (t) setTab(t);
        };
        window.addEventListener("admin-switch-tab", handler);
        return () => window.removeEventListener("admin-switch-tab", handler);
    }, []);

    // Deep-linkable tabs — /admin?tab=trust etc. Kept in two-way sync with the
    // URL so tab links are shareable and browser back/forward works.
    useEffect(() => {
        const urlTab = searchParams.get("tab") || "dashboard";
        if (urlTab !== tab) setTab(urlTab);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchParams]);
    useEffect(() => {
        const urlTab = searchParams.get("tab") || "dashboard";
        if (urlTab === tab) return;
        const next = new URLSearchParams(searchParams);
        if (tab === "dashboard") next.delete("tab"); else next.set("tab", tab);
        setSearchParams(next, { replace: true });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tab]);
    const [cases, setCases] = useState([]);
    const [clients, setClients] = useState([]);
    const [invoices, setInvoices] = useState([]);
    const [loading, setLoading] = useState(true);
    // Drill-down drawer triggered by the 3 header KPIs (Total cases / Clients /
    // Open invoices). Same drawer the Dashboard widget uses — single component
    // so the UX, copy and keyboard handling stay consistent everywhere.
    const [headerDrill, setHeaderDrill] = useState(null);
    const [wipLimit, setWipLimit] = useState(0);

    const load = useCallback(async () => {
        try {
            const [c, cl, inv] = await Promise.all([
                axios.get(`${API}/admin/cases`),
                axios.get(`${API}/admin/clients`),
                axios.get(`${API}/admin/invoices`),
            ]);
            setCases(c.data);
            setClients(cl.data);
            setInvoices(inv.data);
        } catch {
            toast.error("Failed to load admin data");
        } finally {
            setLoading(false);
        }
        // WIP limit is non-critical; fetched separately so a settings 403 (e.g. lacking
        // view_settings perm under preview-as) doesn't break the cases list.
        axios.get(`${API}/admin/settings`)
            .then(({ data }) => setWipLimit(Number(data?.kanban_wip_limit) || 0))
            .catch(() => setWipLimit(0));
    }, []);

    useEffect(() => {
        if (authLoading) return;
        if (!user) {
            nav("/login", { replace: true, state: { from: "/admin" } });
            return;
        }
        if (user.role === "client") {
            nav("/portal", { replace: true });
            return;
        }
        load();
    }, [user, authLoading, nav, load]);

    // Categorized navigation — six logical groups + three standalone tabs.
    // Each entry is either { type: "tab", ... } (renders as a direct tab button)
    // or { type: "group", ..., children: [...] } (renders as a dropdown menu).
    // Permission flags (perm / superadminOnly / platformOnly / ceoOnly) work
    // identically on both top-level tabs and on each child inside a group.
    const isCEO = (user?.email || "").toLowerCase() === "ceo@toothrocket.com";

    // Red badge — pending clinic connection requests (Operations group + Connections item).
    const [connPending, setConnPending] = useState(0);
    useEffect(() => {
        let alive = true;
        const fetchCount = () => axios.get(`${API}/connect/lab/pending-count`)
            .then((r) => { if (alive) setConnPending(r.data.count || 0); })
            .catch(() => {});
        fetchCount();
        const iv = setInterval(fetchCount, 60000);
        window.addEventListener("conn-refresh", fetchCount);
        return () => { alive = false; clearInterval(iv); window.removeEventListener("conn-refresh", fetchCount); };
    }, [tab]);

    const navStructure = useMemo(() => {
        const isVisible = (def) => {
            if (def.platformOnly) return isPlatformAdmin;
            if (def.superadminOnly) return isSuperadmin;
            if (def.ceoOnly) return isCEO;
            return hasPerm(def.perm);
        };

        const all = [
            { type: "tab", id: "dashboard", label: "Dashboard", icon: LayoutDashboard, perm: "view_dashboard" },
            // Compliance pinned to the second slot — Trust Center is one of
            // the most-used surfaces (auditor downloads, recent-activity feed),
            // so it deserves the prime real-estate after Dashboard.
            {
                type: "group", id: "compliance", label: "Compliance", icon: ShieldCheck, accent: "amber",
                children: [
                    { id: "trust", label: "Trust Center", icon: ShieldCheck, perm: null },
                    { id: "support", label: "Support", icon: LifeBuoy, perm: null, superadminOnly: true },
                    { id: "privacy", label: "Data Privacy", icon: ShieldCheck, perm: null, superadminOnly: true },
                    { id: "dr", label: "Disaster Recovery", icon: ServerCrash, perm: null, superadminOnly: true },
                ],
            },
            {
                type: "group", id: "operations", label: "Operations", icon: Activity, accent: "sky",
                children: [
                    { id: "cases", label: t("admin.tab_cases"), icon: Activity, perm: "view_cases" },
                    { id: "scan_inbox", label: "Scan Inbox", icon: Inbox, perm: "view_cases" },
                    { id: "connections", label: "Connections", icon: Link2, perm: "view_cases" },
                    { id: "warranty_claims", label: "Warranty Claims", icon: ShieldCheck, perm: "view_cases" },
                    { id: "clients", label: t("admin.tab_clients"), icon: Users, perm: "view_clients" },
                    { id: "procurement", label: "Procurement", icon: ShoppingCart, perm: "view_procurement" },
                ],
            },
            {
                type: "group", id: "catalog", label: "Catalog", icon: Package, accent: "indigo",
                children: [
                    { id: "products", label: "Products", icon: Package, perm: "view_products" },
                    { id: "technicians", label: "Technicians", icon: Wrench, perm: "view_technicians" },
                    { id: "branding", label: "Branding", icon: ImageIcon, perm: "view_branding" },
                    { id: "media", label: "Media Library", icon: Upload, perm: "view_branding" },
                ],
            },
            {
                type: "group", id: "finance", label: "Finance", icon: PiggyBank, accent: "emerald",
                children: [
                    { id: "finance", label: "Finance", icon: PiggyBank, perm: "view_finance" },
                    { id: "finance_model", label: "AI / Cash Burn", icon: TrendingUp, perm: "view_ai_tools" },
                    { id: "cap_table", label: "Cap table", icon: PiggyBank, perm: "view_ai_tools", ceoOnly: true },
                    { id: "sales", label: "Sales", icon: Megaphone, perm: "view_sales" },
                    { id: "payroll", label: "Payroll", icon: Wallet, perm: "view_payroll" },
                    { id: "doctors", label: "Doctors", icon: Stethoscope, perm: "view_finance" },
                    { id: "credits", label: "Credits", icon: Wallet, perm: "view_credits" },
                    { id: "invoices", label: t("admin.tab_invoices"), icon: Receipt, perm: "view_invoices" },
                    { id: "subscription", label: "Subscription", icon: Crown, perm: null, superadminOnly: true },
                ],
            },
            {
                type: "group", id: "team", label: "Team", icon: Users, accent: "violet",
                children: [
                    { id: "team", label: "Team", icon: Shield, perm: "view_team" },
                    { id: "team_members", label: "About / Founders", icon: Users, perm: null, superadminOnly: true },
                    { id: "feedback", label: "Feedback", icon: MessageSquareHeart, perm: "view_feedback" },
                    { id: "audit", label: "Audit log", icon: Shield, perm: "view_audit" },
                ],
            },
            {
                type: "group", id: "platform", label: "Platform", icon: Building2, accent: "rose",
                children: [
                    { id: "tenants", label: "Toothmatrix", icon: Building2, perm: null, platformOnly: true },
                    { id: "mrr", label: "MRR", icon: TrendingUp, perm: null, platformOnly: true },
                    { id: "database", label: "Database", icon: Database, perm: null, platformOnly: true },
                    { id: "errors", label: "Errors", icon: AlertTriangle, perm: "view_errors" },
                    { id: "marketing", label: "Marketing", icon: Megaphone, perm: null, platformOnly: true },
                ],
            },
            { type: "tab", id: "settings", label: t("admin.tab_settings"), icon: SettingsIcon, perm: "view_settings" },
            { type: "tab", id: "manual", label: "Manual", icon: BookOpen, perm: null },
        ];

        return all
            .map((entry) => {
                if (entry.type === "tab") return isVisible(entry) ? entry : null;
                const visibleChildren = entry.children.filter(isVisible);
                if (visibleChildren.length === 0) return null;
                return { ...entry, children: visibleChildren };
            })
            .filter(Boolean);
    }, [t, hasPerm, isSuperadmin, isPlatformAdmin, isCEO]);

    if (authLoading || loading) return <div className="min-h-[60vh] flex items-center justify-center"><Loader2 className="animate-spin text-slate-500" /></div>;
    if (!user) return null;

    return (
        <div data-testid="admin-page" className="bg-slate-50 min-h-screen">
            <PreviewBanner />
            <CommandPalette />
            {/* Sub-header */}
            <div className="bg-white border-b border-slate-200">
                <div className="max-w-[1280px] mx-auto px-5 lg:px-8 py-8 flex flex-wrap items-end justify-between gap-4">
                    <div>
                        <div className="tr-overline">· {t("admin.console")}</div>
                        <div className="mt-2 flex items-center gap-3 flex-wrap">
                            <h1 className="font-display font-light text-4xl tracking-tight text-slate-900">{t("admin.lab_control")}</h1>
                            <OpsCriticalBadge />
                        </div>
                        <div className="mt-1 text-[13.5px] text-slate-500">{user.email}</div>
                    </div>
                    <div className="flex flex-wrap items-center gap-3">
                        <ManualsMenu />
                        <Stat label={t("admin.total_cases")} value={cases.length} onClick={() => setHeaderDrill({ title: "Total cases", url: "/admin/dashboard/drill?metric=cases_total&limit=200" })} testid="header-stat-cases" />
                        <Stat label={t("admin.clients")} value={clients.length} onClick={() => setHeaderDrill({ title: "All clients", url: "/admin/dashboard/drill?metric=clients_total&limit=200" })} testid="header-stat-clients" />
                        <Stat label={t("admin.open_invoices")} value={invoices.filter((i) => i.status !== "paid").length} onClick={() => setHeaderDrill({ title: "Open invoices", url: "/admin/dashboard/drill?metric=open_invoices&limit=200" })} testid="header-stat-open-invoices" />
                        <ViewAsSwitcher />
                        <button onClick={() => { logout(); nav("/"); }} className="tr-btn-ghost !py-2.5">
                            <LogOut size={15} /> {t("nav.logout")}
                        </button>
                    </div>
                </div>
                <div
                    className="max-w-[1280px] mx-auto px-5 lg:px-8 flex items-stretch gap-x-1 -mb-px overflow-x-auto tr-tabs-scroller"
                    data-testid="admin-tabs"
                >
                    {navStructure.map((entry) => {
                        if (entry.type === "tab") {
                            const Icon = entry.icon;
                            const active = tab === entry.id;
                            return (
                                <button
                                    key={entry.id}
                                    data-testid={`admin-tab-${entry.id}`}
                                    onClick={() => setTab(entry.id)}
                                    className={`flex items-center gap-2 px-3.5 py-3 text-[13.5px] font-medium border-b-2 transition whitespace-nowrap ${
                                        active ? "border-cyan-500 text-slate-900" : "border-transparent text-slate-500 hover:text-slate-900"
                                    }`}
                                >
                                    <Icon size={15} /> {entry.label}
                                </button>
                            );
                        }
                        // Group → dropdown menu (color-accented, hover-to-open)
                        const GroupIcon = entry.icon;
                        const activeChild = entry.children.find((c) => c.id === tab);
                        const groupActive = Boolean(activeChild);
                        const accent = NAV_ACCENTS[entry.accent] || NAV_ACCENTS.sky;
                        const isOpen = openGroup === entry.id;
                        return (
                            <DropdownMenu
                                key={entry.id}
                                open={isOpen}
                                modal={false}
                                onOpenChange={(o) => {
                                    if (o) openNavGroup(entry.id);
                                    else closeNavNow();
                                }}
                            >
                                <DropdownMenuTrigger asChild>
                                    <button
                                        data-testid={`admin-tab-group-${entry.id}`}
                                        onMouseEnter={() => openNavGroup(entry.id)}
                                        onMouseLeave={scheduleCloseNav}
                                        onFocus={() => openNavGroup(entry.id)}
                                        className={`group flex items-center gap-2 px-3.5 py-3 text-[13.5px] font-medium border-b-2 transition whitespace-nowrap outline-none ${
                                            groupActive
                                                ? "border-cyan-500 text-slate-900"
                                                : "border-transparent text-slate-500 hover:text-slate-900 data-[state=open]:text-slate-900"
                                        }`}
                                    >
                                        <GroupIcon size={15} className={`${accent.icon} ${accent.iconHover} transition`} />
                                        <span>{entry.label}</span>
                                        {entry.id === "operations" && connPending > 0 && (
                                            <span data-testid="conn-badge-group" className="min-w-[18px] h-[18px] px-1 rounded-full bg-rose-500 text-white text-[10.5px] font-bold flex items-center justify-center leading-none">{connPending}</span>
                                        )}
                                        {groupActive && activeChild && (
                                            <span className="text-slate-400 font-normal hidden lg:inline">/ {activeChild.label}</span>
                                        )}
                                        <ChevronDown size={13} className="text-slate-400 transition group-data-[state=open]:rotate-180 group-data-[state=open]:text-slate-600" />
                                    </button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent
                                    align="start"
                                    sideOffset={6}
                                    onMouseEnter={() => openNavGroup(entry.id)}
                                    onMouseLeave={scheduleCloseNav}
                                    onPointerDownOutside={closeNavNow}
                                    onEscapeKeyDown={closeNavNow}
                                    onCloseAutoFocus={(e) => {
                                        // Block Radix from restoring focus to the trigger button on close —
                                        // the trigger has `onFocus={openNavGroup}` for keyboard a11y, and
                                        // letting Radix re-focus it after auto-close kicks off an infinite
                                        // close→focus→open loop. Keyboard users can still open via Enter.
                                        e.preventDefault();
                                    }}
                                    className="min-w-[224px] py-1.5 relative overflow-hidden"
                                    data-testid={`admin-tab-group-${entry.id}-menu`}
                                >
                                    {/* Left accent bar — subtle vertical strip in the group's color */}
                                    <span className={`absolute left-0 top-2 bottom-2 w-[3px] rounded-r ${accent.leftBar} opacity-80`} aria-hidden />
                                    <div className={`pl-4 pr-3 pt-1.5 pb-1 text-[10.5px] uppercase tracking-[0.14em] font-semibold ${accent.header}`}>
                                        {entry.label}
                                    </div>
                                    {entry.children.map((child) => {
                                        const ChildIcon = child.icon;
                                        const childActive = tab === child.id;
                                        return (
                                            <DropdownMenuItem
                                                key={child.id}
                                                data-testid={`admin-tab-${child.id}`}
                                                onSelect={() => { setTab(child.id); closeNavNow(); }}
                                                className={`flex items-center gap-2.5 pl-4 pr-3 py-2 text-[13px] cursor-pointer rounded-sm mx-1 ${
                                                    childActive
                                                        ? `${accent.activeBg} text-slate-900 ${accent.activeFocusBg}`
                                                        : "text-slate-600 focus:bg-slate-50 focus:text-slate-900"
                                                }`}
                                            >
                                                <ChildIcon size={14} className={childActive ? accent.activeIcon : "text-slate-400"} />
                                                <span className="flex-1">{child.label}</span>
                                                {child.id === "connections" && connPending > 0 && (
                                                    <span data-testid="conn-badge-item" className="min-w-[18px] h-[18px] px-1 rounded-full bg-rose-500 text-white text-[10.5px] font-bold flex items-center justify-center leading-none">{connPending}</span>
                                                )}
                                                {childActive && <span className={`h-1.5 w-1.5 rounded-full ${accent.activeDot}`} aria-hidden />}
                                            </DropdownMenuItem>
                                        );
                                    })}
                                </DropdownMenuContent>
                            </DropdownMenu>
                        );
                    })}
                </div>
            </div>

            <div className="max-w-[1280px] mx-auto px-5 lg:px-8 py-10">
                {tab === "dashboard" && <Dashboard />}
                {tab === "settings" && <Settings />}
                {/* All other tabs are lazy-chunked. Wrapping them in a single
                    Suspense keeps the fallback consistent regardless of which
                    chunk is loading. Dashboard / Settings stay outside so the
                    most common navigations don't show a loader flash. */}
                <Suspense fallback={<TabLoader />}>
                    {tab === "products" && <Products />}
                    {tab === "technicians" && <Technicians />}
                    {tab === "branding" && <Branding />}
                    {tab === "media" && <MediaLibrary />}
                    {tab === "team_members" && <TeamMembers />}
                    {tab === "manual" && <Manual />}
                    {tab === "feedback" && <Feedback />}
                    {tab === "credits" && <AdminCredits />}
                    {tab === "finance" && <Finance />}
                    {tab === "sales" && <Sales />}
                    {tab === "payroll" && <Payroll />}
                    {tab === "doctors" && <Doctors />}
                    {tab === "team" && <Team />}
                    {tab === "audit" && <AdminAuditLog />}
                    {tab === "support" && <SupportInbox />}
                    {tab === "privacy" && <DataPrivacy />}
                    {tab === "dr" && <DisasterRecovery />}
                    {tab === "trust" && <AdminTrust />}
                    {tab === "finance_model" && <FinanceModel viewMode="finance" />}
                    {tab === "cap_table" && <FinanceModel viewMode="cap_table" />}
                    {tab === "database" && <Database_ />}
                    {tab === "errors" && <Errors />}
                    {tab === "mrr" && <PlatformMetrics />}
                    {tab === "tenants" && <Tenants />}
                    {tab === "marketing" && <Marketing />}
                    {tab === "procurement" && <Procurement />}
                    {tab === "subscription" && <Subscription />}

                    {tab === "cases" && <Cases cases={cases} wipLimit={wipLimit} load={load} />}
                    {tab === "scan_inbox" && <ScanInbox />}
                    {tab === "connections" && <Connections />}
                    {tab === "warranty_claims" && <WarrantyClaims />}
                    {tab === "clients" && <Clients clients={clients} load={load} />}
                    {tab === "invoices" && <Invoices invoices={invoices} load={load} />}
                </Suspense>

                {/* Header-strip KPI drill-down — reuses the same DrillDrawer the
                    Dashboard widget uses, so the UX is consistent. */}
                {headerDrill && (
                    <DrillDrawer
                        title={headerDrill.title}
                        url={headerDrill.url}
                        onClose={() => setHeaderDrill(null)}
                    />
                )}
            </div>
        </div>
    );
}

// Lightweight loader shown while a lazy tab chunk is being fetched. Keeps the
// header / nav stable so the page doesn't jump on first chunk load.
function TabLoader() {
    return (
        <div className="flex items-center justify-center py-24" data-testid="admin-tab-loader" aria-busy="true">
            <Loader2 size={20} className="animate-spin text-cyan-600" />
            <span className="ml-3 font-mono-tr text-[11px] tracking-[0.22em] uppercase text-slate-500">Loading…</span>
        </div>
    );
}

function Stat({ label, value, onClick, testid }) {
    // Base tile uses slate-50 in light, an elevated slate-800/60 in dark so
    // the card pops one step ABOVE the sub-header (which goes to slate-900
    // via the html.dark .bg-white override). Without this explicit dark
    // variant the card collapses into the background — see iter 82 fix.
    const tileBase =
        "px-4 py-2 rounded-xl border border-slate-200 bg-slate-50/50 " +
        "dark:bg-slate-800/60 dark:border-slate-700/70";
    const labelBase = "text-[10px] font-mono-tr tracking-[0.18em] uppercase text-slate-500 dark:text-slate-300";
    const valueBase = "font-display text-xl text-slate-900 tracking-tight dark:text-white";
    if (!onClick) {
        return (
            <div className={tileBase}>
                <div className={labelBase}>{label}</div>
                <div className={valueBase}>{value}</div>
            </div>
        );
    }
    // Drillable variant — keyboard-focusable button so screen-readers + Tab
    // navigation work. Tooltip nudges new admins toward the drill-down feature.
    return (
        <button
            type="button"
            onClick={onClick}
            data-testid={testid}
            title="Click to drill down"
            className={
                `text-left ${tileBase} ` +
                "hover:bg-white hover:border-cyan-300 hover:shadow-sm " +
                "dark:hover:bg-slate-700/70 dark:hover:border-cyan-500/60 " +
                "transition focus:outline-none focus:ring-2 focus:ring-cyan-400"
            }
        >
            <div className={`${labelBase} group-hover:text-cyan-700`}>{label}</div>
            <div className={valueBase}>{value}</div>
        </button>
    );
}

